import { supabase } from './supabase';
import type { SermonAgentUploadRequest } from './kimiChat';

type FinalizeUploadResponse = {
  success?: boolean;
  attachedUrl?: string;
  target?: string;
  error?: string;
};

export const uploadFileForSermonAgent = async (
  uploadRequest: SermonAgentUploadRequest,
  file: File
): Promise<{ attachedUrl: string }> => {
  const mime = file.type?.toLowerCase() || '';
  const isAccepted = uploadRequest.acceptedMimePrefixes.some((prefix) =>
    mime.startsWith(prefix.toLowerCase())
  );
  if (!isAccepted) {
    throw new Error('Selected file type is not allowed for this upload request.');
  }
  if (file.size > uploadRequest.maxBytes) {
    throw new Error(`File is too large. Max allowed size is ${Math.floor(uploadRequest.maxBytes / 1024 / 1024)}MB.`);
  }

  const { error: uploadError } = await supabase.storage
    .from(uploadRequest.bucket)
    .uploadToSignedUrl(uploadRequest.path, uploadRequest.token, file, {
      upsert: false,
      contentType: file.type || undefined,
    });

  if (uploadError) {
    throw new Error(uploadError.message || 'Upload failed.');
  }

  const { data, error } = await supabase.functions.invoke<FinalizeUploadResponse>('sermon-upload-session', {
    body: {
      operation: 'finalize',
      sermonId: uploadRequest.sermonId,
      target: uploadRequest.target,
      bucket: uploadRequest.bucket,
      path: uploadRequest.path,
      fileName: file.name || uploadRequest.suggestedFileName,
      mimeType: file.type || null,
    },
  });

  if (error) {
    throw new Error(error.message || 'Unable to finalize uploaded file.');
  }
  if (!data?.success || !data.attachedUrl) {
    throw new Error(data?.error || 'Unable to attach uploaded file.');
  }

  return { attachedUrl: data.attachedUrl };
};
